import HttpFactory from "~/repository/factory";
import type {
  ConfigureMeetingLinkPayload,
  MeetingRoomDto,
  SlugAvailabilityResult,
  SlugResolveResult,
} from "~/types/meeting";

/** Timeout padrão (ms) das chamadas da sala — evita UI travar. */
const MEETING_TIMEOUT = 8_000;

class MeetingModule extends HttpFactory {
  private RESOURCE = "/meeting";

  /** Cria ou substitui o link de reunião do lead. */
  async configureLink(
    leadId: string,
    payload: ConfigureMeetingLinkPayload,
  ): Promise<MeetingRoomDto> {
    return await this.post<MeetingRoomDto>(
      `${this.RESOURCE}/lead/${leadId}/link`,
      payload,
    );
  }

  /** Resolve um slug público — endpoint aberto, usado em /meet/:slug. */
  async resolveSlug(slug: string): Promise<SlugResolveResult> {
    return await this.get<SlugResolveResult>(
      `${this.RESOURCE}/slug/${encodeURIComponent(slug)}/resolve`,
    );
  }

  /** Checa disponibilidade em tempo real enquanto o corretor digita. */
  async checkAvailability(slug: string): Promise<SlugAvailabilityResult> {
    return await this.get<SlugAvailabilityResult>(
      `${this.RESOURCE}/slug/${encodeURIComponent(slug)}/availability`,
    );
  }

  /** Retorna a sala ativa do lead (ou null). */
  async getActiveRoomForLead(leadId: string): Promise<MeetingRoomDto | null> {
    const result = await this.get<MeetingRoomDto | null>(
      `${this.RESOURCE}/lead/${leadId}/active-room`,
    );
    // Guard: resposta malformada (HTML → undefined, {} sem slug) → trata como nenhuma sala.
    if (!result || typeof result !== 'object' || !result.slug) return null;
    return result;
  }

  /** Corretor entra (ou reentra) na sala. Retorna token Twilio + metadata. */
  async startAsHost(slug: string): Promise<{
    token: string;
    roomName: string;
    sessionId: string;
    participantId: string;
    slug: string;
    pendingKnocks: Array<{ participantId: string; displayName: string; knockedAt: string; isAuthenticatedGuest: boolean }>;
  }> {
    return await this.post(
      `${this.RESOURCE}/slug/${encodeURIComponent(slug)}/start`,
      undefined,
      { timeout: MEETING_TIMEOUT },
    );
  }

  /** Desativa (arquiva) o link da sala e encerra a session ativa. */
  async deactivateLink(slug: string): Promise<{ status: 'deactivated' }> {
    return await this.post(
      `${this.RESOURCE}/slug/${encodeURIComponent(slug)}/deactivate`,
      undefined,
      { timeout: MEETING_TIMEOUT },
    );
  }

  /** Encerra a reunião (botão do corretor). */
  async endSession(slug: string): Promise<{ status: 'ended' }> {
    return await this.post(
      `${this.RESOURCE}/slug/${encodeURIComponent(slug)}/end`,
      undefined,
      { timeout: MEETING_TIMEOUT },
    );
  }

  /** Guest bate na porta da sala (público). Retorna guestToken + IDs. */
  async guestKnock(
    slug: string,
    payload: { name: string },
  ): Promise<{
    participantId: string;
    sessionId: string;
    guestToken: string;
  }> {
    return await this.post(
      `${this.RESOURCE}/slug/${encodeURIComponent(slug)}/guest-knock`,
      payload,
    );
  }

  /** Guest autenticado bate na porta (requer sessão Kratos). Retorna guestToken + IDs. */
  async authenticatedGuestKnock(
    slug: string,
    payload: { name: string },
  ): Promise<{
    participantId: string;
    sessionId: string;
    guestToken: string;
  }> {
    return await this.post(
      `${this.RESOURCE}/slug/${encodeURIComponent(slug)}/authenticated-guest-knock`,
      payload,
    );
  }

  /** Corretor autoriza um guest. */
  async authorizeParticipant(
    participantId: string,
  ): Promise<{ status: 'authorized' }> {
    return await this.post(
      `${this.RESOURCE}/participant/${encodeURIComponent(participantId)}/authorize`,
    );
  }

  /** Corretor recusa um guest. */
  async rejectParticipant(
    participantId: string,
  ): Promise<{ status: 'rejected' }> {
    return await this.post(
      `${this.RESOURCE}/participant/${encodeURIComponent(participantId)}/reject`,
    );
  }

  /** Corretor autoriza + já marca como lead num único round-trip. */
  async authorizeAndMarkAsLead(
    participantId: string,
  ): Promise<{ status: 'authorized' }> {
    return await this.post(
      `${this.RESOURCE}/participant/${encodeURIComponent(participantId)}/authorize`,
      { markAsLead: true },
    );
  }

  /** Atomicamente marca/desmarca lead da session (participantId null = desmarcar). */
  async setSessionLead(
    sessionId: string,
    participantId: string | null,
  ): Promise<{ leadParticipantId: string | null }> {
    return await this.patch(
      `${this.RESOURCE}/session/${encodeURIComponent(sessionId)}/lead`,
      { participantId },
    );
  }

  /** Corretor envia mensagem de chat — persistência paralela ao data track. */
  async sendChatAsHost(
    sessionId: string,
    text: string,
  ): Promise<{
    id: string;
    sentAt: string;
    participantId: string;
    text: string;
  }> {
    return await this.post(
      `${this.RESOURCE}/session/${encodeURIComponent(sessionId)}/chat`,
      { text },
    );
  }

  /** Guest envia mensagem de chat — requer guest token no fetcher. */
  async sendChatAsGuest(
    sessionId: string,
    text: string,
    guestToken: string,
  ): Promise<{
    id: string;
    sentAt: string;
    participantId: string;
    text: string;
  }> {
    return await this.post(
      `${this.RESOURCE}/session/${encodeURIComponent(sessionId)}/chat-guest`,
      { text },
      { headers: { Authorization: `Bearer ${guestToken}` } },
    );
  }

  async getChatHistory(sessionId: string): Promise<
    Array<{
      id: string;
      session_id: string;
      participant_id: string;
      twilio_identity: string;
      participant_display_name: string;
      text: string;
      sent_at: string;
    }>
  > {
    return await this.get(
      `${this.RESOURCE}/session/${encodeURIComponent(sessionId)}/chat`,
    );
  }

  async startRecording(slug: string): Promise<{
    segmentId: string;
    startedAt: string;
  }> {
    return await this.post(
      `${this.RESOURCE}/slug/${encodeURIComponent(slug)}/recording/start`,
    );
  }

  async stopRecording(slug: string): Promise<{ state: 'absent' | 'recording' | 'processing' | 'completed' }> {
    return await this.post(
      `${this.RESOURCE}/slug/${encodeURIComponent(slug)}/recording/stop`,
    );
  }

  /**
   * Guest já autorizado reingressa após refresh de página sem nova aprovação.
   */
  async rejoinAsGuest(
    participantId: string,
    guestToken: string,
  ): Promise<{
    twilioAccessToken: string;
    roomName: string;
    identity: string;
    displayName: string;
    sessionId: string;
    isLead: boolean;
    leadIdentity: string | null;
    recordingState: 'absent' | 'recording' | 'processing' | 'completed';
    recordingStartedAt: string | null;
    sessionStartedAt: string | null;
    newGuestToken: string;
  }> {
    return await this.post(
      `${this.RESOURCE}/participant/${encodeURIComponent(participantId)}/rejoin`,
      undefined,
      { headers: { Authorization: `Bearer ${guestToken}` }, timeout: MEETING_TIMEOUT },
    );
  }

  /**
   * Lead aceitou o modal de consentimento de gravação.
   * Retorna o token Twilio e inicia a gravação na session.
   */
  async consentAndJoin(
    participantId: string,
    guestToken: string,
  ): Promise<{ twilioAccessToken: string; roomName: string; identity: string }> {
    return await this.post(
      `${this.RESOURCE}/participant/${encodeURIComponent(participantId)}/consent-and-join`,
      undefined,
      { headers: { Authorization: `Bearer ${guestToken}` }, timeout: MEETING_TIMEOUT },
    );
  }
}

export default MeetingModule;
